import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useConsole, useRead } from './context';
import { Empty, Failure, Loading } from './bits';
import { Pager } from './Fleet';
import { Feed } from './business/activity/Feed';
import { useSettled } from './business/activity/useSettled';
import { useT } from '../messages';

/**
 * **The Log** — who did what, across every business, in sentences (console
 * redesign, phase B6).
 *
 * The same feed as a business's Activity tab (`business/activity/Feed.tsx`),
 * with the business named and linked on every line, and four filters that
 * apply as they are changed: which business, which person, what kind of
 * change, and since when. There is no Search button — a filter is a view of
 * the list, not a form being submitted — and the text boxes wait for the
 * operator to stop typing before they ask.
 *
 * **Reading the log is itself recorded.** `admin-api` writes a row for every
 * read, with the filter it used, after the read — so a read never appears in
 * its own result, and two reads in a row show the first. The note under the
 * filters says so in plain words, because an operator surprised by their own
 * name in the list should find the explanation before looking for a bug. Those
 * rows, and every other read, are "only looking", and hidden unless asked for
 * — as on the Activity tab.
 *
 * **The kinds of change are words, and each is a handful of action codes** —
 * the server matches a comma list, with `noun.*` for every action under a noun
 * — so the operator picks "Keys and partners" rather than typing `key.issue`.
 * The exact code is still on every line, in its raw record.
 */

type Kind =
  | 'all'
  | 'stage'
  | 'features'
  | 'settings'
  | 'catalog'
  | 'website'
  | 'connections'
  | 'people'
  | 'data';

/** What each kind of change asks the server for. */
export const KIND_ACTIONS: Record<Exclude<Kind, 'all'>, string> = {
  stage: 'tenant.create,tenant.set_status,tenant.protect,tenant.delete',
  features: 'tenant.modules',
  settings:
    'tenant.settings,tenant.scheduling_policy,tenant.locales,booking.*,till.*,shop.*,' +
    'ordering.*,messaging.*,scheduling.*,payment.*,fulfillment.*,departures.*,lodging.*',
  catalog: 'catalog.*',
  website: 'branding.*,domain.*,intake.*',
  connections: 'key.*,webhook.*,delivery.*,concierge.*',
  people: 'staff.*,operator.*,tenant.support_session',
  data:
    'import.*,tenant.exported,tenant.export_landed,job.*,roster.*,data_request.*,' +
    'client.anonymize,album.*',
};

const KINDS: readonly Kind[] = [
  'all',
  'stage',
  'features',
  'settings',
  'catalog',
  'website',
  'connections',
  'people',
  'data',
];

type Since = 'any' | 'day' | 'week' | 'month';
const SINCE_HOURS: Record<Exclude<Since, 'any'>, number> = {
  day: 24,
  week: 24 * 7,
  month: 24 * 30,
};

export function Audit({ tenantId }: { tenantId?: string | undefined }): ReactNode {
  const api = useApi();
  const t = useT();
  const { config } = useConsole();

  const [business, setBusiness] = useState(tenantId ?? '');
  const [person, setPerson] = useState('');
  const [kind, setKind] = useState<Kind>('all');
  const [since, setSince] = useState<Since>('any');
  const [looks, setLooks] = useState(false);

  // Typed filters wait for a pause; picked ones apply at once.
  const businessAsked = useSettled(business.trim());
  const personAsked = useSettled(person.trim());

  // The page is remembered per filter, so a new filter starts at its first
  // page without a wasted read of the old page under the new filter.
  const filterKey = JSON.stringify([businessAsked, personAsked, kind, since, looks]);
  const [paged, setPaged] = useState({ key: filterKey, offset: 0 });
  const offset = paged.key === filterKey ? paged.offset : 0;
  const setOffset = (next: number): void => setPaged({ key: filterKey, offset: next });

  const page = useRead(
    () =>
      api.audit({
        ...(businessAsked === '' ? {} : { tenant_id: businessAsked }),
        ...(personAsked === '' ? {} : { person: personAsked }),
        ...(kind === 'all' ? {} : { action: KIND_ACTIONS[kind] }),
        ...(since === 'any'
          ? {}
          : { since: new Date(Date.now() - SINCE_HOURS[since] * 3_600_000).toISOString() }),
        ...(looks ? {} : { changes_only: true }),
        limit: config.pageSize,
        offset,
      }),
    [filterKey, offset, config.pageSize],
  );

  const filtered = business !== '' || person !== '' || kind !== 'all' || since !== 'any' || looks;

  return (
    <div className="view">
      <header className="view__head">
        <div>
          <h2>{t('log.title')}</h2>
          <p className="muted">{t('log.lede')}</p>
        </div>
      </header>

      <section className="pane alog">
        <div className="alog__filters">
          <label className="inline-field">
            <span>{t('log.filter.business')}</span>
            <input
              type="search"
              value={business}
              onChange={(e) => setBusiness(e.target.value)}
              placeholder={t('log.filter.businessHint')}
            />
          </label>
          <label className="inline-field">
            <span>{t('log.filter.person')}</span>
            <input
              type="search"
              value={person}
              onChange={(e) => setPerson(e.target.value)}
              placeholder={t('log.filter.personHint')}
            />
          </label>
          <label className="inline-field">
            <span>{t('log.filter.kind')}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {kindLabel(t, k)}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-field">
            <span>{t('log.filter.since')}</span>
            <select value={since} onChange={(e) => setSince(e.target.value as Since)}>
              <option value="any">{t('log.since.any')}</option>
              <option value="day">{t('log.since.day')}</option>
              <option value="week">{t('log.since.week')}</option>
              <option value="month">{t('log.since.month')}</option>
            </select>
          </label>
          <label className="inline-field">
            <input type="checkbox" checked={looks} onChange={(e) => setLooks(e.target.checked)} />
            <span>{t('activity.looks')}</span>
          </label>
          {filtered ? (
            <button
              type="button"
              className="button"
              onClick={() => {
                setBusiness('');
                setPerson('');
                setKind('all');
                setSince('any');
                setLooks(false);
              }}
            >
              {t('log.clear')}
            </button>
          ) : null}
        </div>

        <p className="muted alog__note">{t('log.selfNote')}</p>

        {page.failure !== null ? <Failure failure={page.failure} /> : null}

        {page.value === null ? (
          page.loading ? (
            <Loading what={t('log.loading')} />
          ) : null
        ) : page.value.entries.length === 0 ? (
          <Empty>{t('log.empty')}</Empty>
        ) : (
          <>
            <Feed
              showBusiness
              entries={page.value.entries.map((e) => ({
                id: e.audit_id,
                at: e.at,
                actor: e.actor,
                action: e.action,
                target: e.target,
                detail: e.detail,
                business:
                  e.tenant_id === null ? null : { id: e.tenant_id, name: e.tenant_name ?? null },
              }))}
            />
            <Pager
              count={page.value.entries.length}
              limit={page.value.limit}
              offset={page.value.offset}
              busy={page.loading}
              onOffset={setOffset}
            />
          </>
        )}
      </section>
    </div>
  );
}

function kindLabel(t: ReturnType<typeof useT>, kind: Kind): string {
  switch (kind) {
    case 'all':
      return t('log.kind.all');
    case 'stage':
      return t('log.kind.stage');
    case 'features':
      return t('log.kind.features');
    case 'settings':
      return t('log.kind.settings');
    case 'catalog':
      return t('log.kind.catalog');
    case 'website':
      return t('log.kind.website');
    case 'connections':
      return t('log.kind.connections');
    case 'people':
      return t('log.kind.people');
    case 'data':
      return t('log.kind.data');
  }
}
